import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ClipboardList, Pencil, Sparkles, X } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import { Surface } from "../../../../shared/ui/primitives";
import {
    INTAKE_FIELDS,
    intakeField,
    validateIntakeValue,
    type IntakeFieldKey,
    type IntakeProfile,
    type IntakeProfileInput,
    type IntakeSuggestion,
} from "../../domain/IntakeProfile";

const CONTROL_CLASS = "field-base field-default w-full px-4 text-sm";

interface Props {
    data?: IntakeProfile;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    /** Saves the fields that changed. */
    onSave: (values: IntakeProfileInput) => Promise<unknown>;
    isSaving: boolean;
    /**
     * `value` is the therapist's edit; absent to keep what was proposed.
     * Without these two the card is the values alone, as a centre sees them:
     * no suggestions, and nothing said about how they are made.
     */
    onAccept?: (suggestionId: string, value?: string) => Promise<unknown>;
    onDismiss?: (suggestionId: string) => Promise<unknown>;
    /** The age from the patient's date of birth, when it is known. */
    ageFromBirthDate?: number | null;
    headingLevel?: "h3" | "h4";
    className?: string;
}

/**
 * What the card says up front about how the profile gets filled in, so the
 * therapist reads it before the fields; once suggestions are waiting, they say
 * it themselves. Nothing for a card that cannot decide on suggestions.
 */
function memoryNoteKey(data: IntakeProfile, decides: boolean): string | null {
    if (!decides) return null;
    if (!data.suggestions_available) {
        return data.suggestions_unavailable_reason === "tool_not_adopted"
            ? "patient.intake.suggestionsNeedTool"
            : "patient.intake.suggestionsUnavailable";
    }
    return data.suggestions.length === 0 ? "patient.intake.suggestionsWaiting" : null;
}

/** The i18n key of what a refused write says. */
function refusal(error: unknown): string {
    const code = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    if (code === "intake_value_invalid") return "patient.intake.errors.valueInvalid";
    if (code === "intake_suggestion_not_found") return "patient.intake.errors.suggestionGone";
    return "patient.intake.errors.generic";
}

function IntakeInput({
    fieldKey,
    id,
    value,
    onChange,
    error,
}: {
    fieldKey: IntakeFieldKey;
    id: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
}) {
    const { t } = useTranslation();
    const field = intakeField(fieldKey);
    const errorId = `${id}-error`;
    const described = { "aria-invalid": Boolean(error), "aria-describedby": error ? errorId : undefined };

    let control;
    if (field?.kind === "long_text") {
        control = (
            <textarea id={id} rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={`${CONTROL_CLASS} py-2.5`} {...described} />
        );
    } else {
        control = (
            <Input
                id={id}
                type={field?.kind === "number" ? "number" : "text"}
                inputMode={field?.kind === "number" ? "numeric" : undefined}
                min={field?.kind === "number" ? "0" : undefined}
                max={field?.max?.toString()}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                error={Boolean(error)}
                {...described}
            />
        );
    }

    return (
        <div className={field?.kind === "long_text" ? "sm:col-span-2" : undefined}>
            <Label htmlFor={id}>{t(field?.labelKey ?? fieldKey)}</Label>
            {control}
            {error && (
                <p id={errorId} className="mt-1 text-xs text-status-critical">
                    {t(error)}
                </p>
            )}
        </div>
    );
}

function SuggestionItem({
    suggestion,
    onAccept,
    onDismiss,
}: {
    suggestion: IntakeSuggestion;
    onAccept: (value?: string) => Promise<void>;
    onDismiss: () => Promise<void>;
}) {
    const { t } = useTranslation();
    const id = useId();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(suggestion.value);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const label = t(intakeField(suggestion.field)?.labelKey ?? suggestion.field);
    const shown = suggestion.value;

    const run = async (action: () => Promise<void>) => {
        setBusy(true);
        try {
            await action();
        } finally {
            setBusy(false);
        }
    };

    const acceptEdited = async () => {
        const problem = validateIntakeValue(suggestion.field, draft) ?? (draft.trim() ? null : "patient.intake.errors.valueRequired");
        setError(problem);
        if (!problem) await run(() => onAccept(draft.trim()));
    };

    return (
        <li className="flex flex-col gap-2 py-3">
            <div className="text-sm">
                <span className="font-medium text-content-primary">{label}:</span>{" "}
                <span className="text-content-primary">{shown}</span>
            </div>
            {suggestion.evidence && (
                <blockquote className="border-l-2 border-border-subtle pl-3 text-xs italic text-content-secondary">
                    “{suggestion.evidence}”
                </blockquote>
            )}
            {editing ? (
                <form
                    noValidate
                    className="flex flex-col gap-2"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void acceptEdited();
                    }}
                >
                    <IntakeInput fieldKey={suggestion.field} id={`${id}-edit`} value={draft} onChange={setDraft} error={error ?? undefined} />
                    <div className="flex flex-wrap justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={busy}>
                            {t("common.cancel")}
                        </Button>
                        <Button size="sm" type="submit" loading={busy} disabled={busy}>
                            {t("patient.intake.acceptEdited")}
                        </Button>
                    </div>
                </form>
            ) : (
                // On a phone: dismiss and edit side by side, accept across the full width below.
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void run(onDismiss)}
                        disabled={busy}
                        startIcon={<X size={14} aria-hidden="true" />}
                        aria-label={t("patient.intake.dismissLabel", { field: label })}
                    >
                        {t("patient.intake.dismiss")}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing(true)}
                        disabled={busy}
                        startIcon={<Pencil size={14} aria-hidden="true" />}
                        aria-label={t("patient.intake.editLabel", { field: label })}
                    >
                        {t("patient.intake.editSuggestion")}
                    </Button>
                    <Button
                        size="sm"
                        className="col-span-2"
                        onClick={() => void run(() => onAccept())}
                        loading={busy}
                        disabled={busy}
                        startIcon={<Check size={14} aria-hidden="true" />}
                        aria-label={t("patient.intake.acceptLabel", { field: label })}
                    >
                        {t("patient.intake.accept")}
                    </Button>
                </div>
            )}
        </li>
    );
}

/**
 * The patient's intake profile ("ficha"): age, family, children, occupation,
 * who they live with, what brings them and what they ask for. Edited in
 * place; what the session analysis proposed waits below for the therapist to
 * accept, edit or dismiss.
 */
export default function IntakeProfileCard({
    data,
    isLoading,
    isError,
    onRetry,
    onSave,
    isSaving,
    onAccept,
    onDismiss,
    ageFromBirthDate,
    headingLevel = "h3",
    className,
}: Props) {
    const { t } = useTranslation();
    const idPrefix = useId();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<Partial<Record<IntakeFieldKey, string>>>({});
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<IntakeFieldKey, string>>>({});
    const [formError, setFormError] = useState<string | null>(null);
    const [suggestionError, setSuggestionError] = useState<string | null>(null);

    const Heading = headingLevel;
    const decides = Boolean(onAccept && onDismiss);
    const title = (
        <Heading className="flex items-center gap-2 text-base font-semibold text-content-primary">
            <ClipboardList className="h-5 w-5 text-content-tertiary" aria-hidden="true" />
            {t("patient.intake.title")}
        </Heading>
    );

    if (isLoading || isError || !data) {
        return (
            <Surface as="section" className={className}>
                {title}
                {isError ? (
                    <div role="alert" className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
                        <span className="text-status-critical">{t("patient.intake.errorLoading")}</span>
                        <Button size="sm" variant="outline" onClick={onRetry}>
                            {t("patient.intake.retry")}
                        </Button>
                    </div>
                ) : (
                    <p role="status" className="mt-3 text-sm text-content-secondary">
                        {t("common.loading")}
                    </p>
                )}
            </Surface>
        );
    }

    const stored = (key: IntakeFieldKey) => data.values[key]?.value ?? "";

    const startEditing = () => {
        setDraft(Object.fromEntries(INTAKE_FIELDS.map(({ key }) => [key, stored(key)])));
        setFieldErrors({});
        setFormError(null);
        setEditing(true);
    };

    const change = (key: IntakeFieldKey, value: string) => {
        setDraft((current) => ({ ...current, [key]: value }));
        setFieldErrors((current) => ({ ...current, [key]: undefined }));
    };

    const handleSubmit = async () => {
        setFormError(null);
        const errors: Partial<Record<IntakeFieldKey, string>> = {};
        for (const { key } of INTAKE_FIELDS) {
            const problem = validateIntakeValue(key, draft[key] ?? "");
            if (problem) errors[key] = problem;
        }
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) return;

        // Only what changed is sent: the server leaves absent fields alone.
        const changes: IntakeProfileInput = {};
        for (const { key } of INTAKE_FIELDS) {
            const next = (draft[key] ?? "").trim();
            if (next !== stored(key)) changes[key] = next || null;
        }
        if (Object.keys(changes).length === 0) {
            setEditing(false);
            return;
        }
        try {
            await onSave(changes);
            setEditing(false);
        } catch (err) {
            setFormError(refusal(err));
        }
    };

    const decide = async (action: () => Promise<unknown>) => {
        setSuggestionError(null);
        try {
            await action();
        } catch (err) {
            setSuggestionError(refusal(err));
        }
    };

    if (editing) {
        return (
            <Surface as="section" className={className}>
                <form
                    noValidate
                    className="flex flex-col gap-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void handleSubmit();
                    }}
                >
                    {title}
                    <div className="grid gap-4 sm:grid-cols-2">
                        {INTAKE_FIELDS.map(({ key }) => (
                            <IntakeInput
                                key={key}
                                fieldKey={key}
                                id={`${idPrefix}-${key}`}
                                value={draft[key] ?? ""}
                                onChange={(value) => change(key, value)}
                                error={fieldErrors[key]}
                            />
                        ))}
                    </div>
                    {formError && (
                        <p role="alert" className="text-sm text-status-critical">
                            {t(formError)}
                        </p>
                    )}
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setEditing(false)} disabled={isSaving}>
                            {t("common.cancel")}
                        </Button>
                        <Button type="submit" loading={isSaving} disabled={isSaving}>
                            {t("common.save")}
                        </Button>
                    </div>
                </form>
            </Surface>
        );
    }

    const memoryNote = memoryNoteKey(data, decides);

    return (
        <Surface as="section" className={className}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                {title}
                <Button size="sm" variant="outline" onClick={startEditing} className="whitespace-nowrap" startIcon={<Pencil size={14} aria-hidden="true" />}>
                    {t("patient.intake.edit")}
                </Button>
            </div>
            {memoryNote && (
                <p className="mt-3 flex items-start gap-2.5 rounded-xl bg-status-info-tint px-3.5 py-3 text-sm font-medium text-status-info-on-tint">
                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{t(memoryNote)}</span>
                </p>
            )}
            <dl className="mt-3 divide-y divide-border-subtle">
                {INTAKE_FIELDS.map(({ key, labelKey }) => {
                    const shown = data.values[key]?.value || null;
                    const fromBirthDate = key === "age" && ageFromBirthDate != null ? ageFromBirthDate : null;
                    return (
                        <div key={key} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3 py-2.5 text-sm">
                            <dt className="text-content-secondary">{t(labelKey)}</dt>
                            <dd className="flex flex-wrap items-center gap-2 whitespace-pre-line break-words text-content-primary">
                                {fromBirthDate != null ? (
                                    <span>{t("patient.intake.ageFromBirthDate", { age: fromBirthDate })}</span>
                                ) : (
                                    shown ?? <span className="text-content-tertiary">—</span>
                                )}
                            </dd>
                        </div>
                    );
                })}
            </dl>

            {onAccept && onDismiss && data.suggestions.length > 0 && (
                <section aria-labelledby={`${idPrefix}-suggestions`} className="mt-4 rounded-2xl bg-status-info-tint p-4">
                    <h4 id={`${idPrefix}-suggestions`} className="flex items-center gap-2 text-sm font-semibold text-status-info-on-tint">
                        <Sparkles className="h-4 w-4" aria-hidden="true" />
                        {t("patient.intake.suggestionsTitle", { count: data.suggestions.length })}
                    </h4>
                    <p className="mt-1 text-xs text-content-secondary">{t("patient.intake.suggestionsHint")}</p>
                    {suggestionError && (
                        <p role="alert" className="mt-2 text-sm text-status-critical">
                            {t(suggestionError)}
                        </p>
                    )}
                    <ul className="mt-2 divide-y divide-border-subtle">
                        {data.suggestions.map((suggestion) => (
                            <SuggestionItem
                                key={suggestion.id}
                                suggestion={suggestion}
                                onAccept={(value) => decide(() => onAccept(suggestion.id, value))}
                                onDismiss={() => decide(() => onDismiss(suggestion.id))}
                            />
                        ))}
                    </ul>
                </section>
            )}
        </Surface>
    );
}
